import React, { useEffect, useState } from 'react'
import { Box, useTheme } from '@mui/material'
import ProgressDot from './ProgressDot'
import { getCircleColor } from '../utils'

interface ProgressDotsProps {
  dotCount?: number
  dotSize?: number
  activeColor?: string
  trailColor?: string
  inactiveColor?: string
  intervalMs?: number
}

/**
 * ProgressDots Component shows a series of dots that animate to indicate progress.
 * The active dot moves back and forth, reversing direction at the ends, with a trailing dot to enhance the effect.
 */
const ProgressDots: React.FC<ProgressDotsProps> = ({ dotCount = 7, dotSize = 16, activeColor, trailColor, inactiveColor, intervalMs = 1000 }) => {
  const [state, setState] = useState<{ activeIndex: number; direction: 'right' | 'left' }>({
    activeIndex: 0,
    direction: 'right',
  })
  const theme = useTheme()

  activeColor = activeColor || theme.palette.primary.main
  trailColor = trailColor || theme.palette.primary.light
  inactiveColor = inactiveColor || theme.palette.shades.shade03

  useEffect(() => {
    const handleStateUpdate = (): void => {
      setState(prevState => {
        const { activeIndex, direction } = prevState
        const next = activeIndex + (direction === 'right' ? 1 : -1)
        if (next >= dotCount || next < 0) {
          return {
            activeIndex,
            direction: direction === 'right' ? 'left' : 'right',
          }
        }
        return {
          activeIndex: next,
          direction,
        }
      })
    }

    /* start timer for state updates */
    const intervalId = setInterval(handleStateUpdate, intervalMs)

    return () => clearInterval(intervalId)
  }, [dotCount, intervalMs])

  return (
    <Box display="flex" gap={1} justifyContent="center" alignItems="center">
      {Array.from({ length: dotCount }).map((_, idx) => {
        return (
          <ProgressDot
            key={`progress-dot-${idx}`}
            size={dotSize}
            color={getCircleColor(idx, state.direction, state.activeIndex, activeColor, trailColor, inactiveColor)}
          />
        )
      })}
    </Box>
  )
}

export default ProgressDots
